/**
 * The measure registry: one entry per effect measure, saying which study data forms can give it,
 * the scale it is pooled on, how values return to the display scale, and whether it compares two
 * groups. Owner: core. Implements CORE-300, CORE-301, CORE-334 and CORE-335.
 *
 * Adding a measure means adding one entry here and one case in ./effects (studyEffect). Nothing
 * else in the engine switches on the measure name except Mantel-Haenszel (binary only).
 */
import type { AnalysisStudy, BinaryMeasure, Measure, StudyKind } from './types';

/**
 * The analysis-scale transformation of a measure:
 * - identity: pooled as is (RD, MD, SMD, GEN, PR, IR)
 * - log: natural log (RR, OR, HR, IRR, GENLOG, PLN, IRLN), back-transformed with exp
 * - logit: log odds (PLO), back-transformed with the inverse logit
 * - freeman_tukey: Freeman-Tukey double arcsine (PFT), back-transformed with metafor transf.ipft
 * - fisher_z: Fisher's z (ZCOR), back-transformed with tanh
 */
export type MeasureScale = 'identity' | 'log' | 'logit' | 'freeman_tukey' | 'fisher_z';

/** What the measure describes, which decides the input forms and the evidence page's wording. */
export type MeasureFamily = 'binary' | 'continuous' | 'reported' | 'proportion' | 'rate' | 'correlation';

export interface MeasureInfo {
  measure: Measure;
  /** Sentence-case name, e.g. "Risk ratio". */
  name: string;
  family: MeasureFamily;
  /** True when the measure compares two groups (intervention or exposed against control or reference). */
  comparative: boolean;
  scale: MeasureScale;
  /** Study data forms that can give this measure. */
  studyKinds: readonly StudyKind[];
  /**
   * Value of "no effect" on the analysis scale, or null when the measure has no null hypothesis of
   * interest (a pooled proportion or rate is an absolute quantity, not a comparison).
   */
  nullValue: number | null;
  /** True when the measure is pooled from tables by Mantel-Haenszel as well as by inverse variance. */
  mantelHaenszel: boolean;
  /** True when the continuity correction option changes the per-study values. */
  usesContinuityCorrection: boolean;
}

const COMPARATIVE_REPORTED: readonly StudyKind[] = ['reported'];

function info(
  measure: Measure,
  name: string,
  family: MeasureFamily,
  scale: MeasureScale,
  studyKinds: readonly StudyKind[],
  extra: { comparative?: boolean; nullValue?: number | null; mh?: boolean; cc?: boolean } = {},
): MeasureInfo {
  return Object.freeze({
    measure,
    name,
    family,
    comparative: extra.comparative ?? true,
    scale,
    studyKinds: Object.freeze([...studyKinds]),
    nullValue: extra.nullValue === undefined ? 0 : extra.nullValue,
    mantelHaenszel: extra.mh ?? false,
    usesContinuityCorrection: extra.cc ?? false,
  });
}

/** Every measure, in the order the evidence page lists them. Frozen. */
export const MEASURES: Readonly<Record<Measure, MeasureInfo>> = Object.freeze({
  RR: info('RR', 'Risk ratio', 'binary', 'log', ['binary', ...COMPARATIVE_REPORTED], { mh: true, cc: true }),
  OR: info('OR', 'Odds ratio', 'binary', 'log', ['binary', ...COMPARATIVE_REPORTED], { mh: true, cc: true }),
  RD: info('RD', 'Risk difference', 'binary', 'identity', ['binary', ...COMPARATIVE_REPORTED], { mh: true, cc: true }),
  MD: info('MD', 'Mean difference', 'continuous', 'identity', ['continuous', ...COMPARATIVE_REPORTED]),
  SMD: info('SMD', 'Standardised mean difference', 'continuous', 'identity', ['continuous', ...COMPARATIVE_REPORTED]),
  HR: info('HR', 'Hazard ratio', 'reported', 'log', COMPARATIVE_REPORTED),
  IRR: info('IRR', 'Incidence rate ratio', 'reported', 'log', COMPARATIVE_REPORTED),
  GENLOG: info('GENLOG', 'Ratio (generic)', 'reported', 'log', COMPARATIVE_REPORTED),
  GEN: info('GEN', 'Difference (generic)', 'reported', 'identity', COMPARATIVE_REPORTED),
  PR: info('PR', 'Proportion', 'proportion', 'identity', ['proportion'], { comparative: false, nullValue: null, cc: true }),
  PLN: info('PLN', 'Proportion (log scale)', 'proportion', 'log', ['proportion'], {
    comparative: false,
    nullValue: null,
    cc: true,
  }),
  PLO: info('PLO', 'Proportion (logit scale)', 'proportion', 'logit', ['proportion'], {
    comparative: false,
    nullValue: null,
    cc: true,
  }),
  PFT: info('PFT', 'Proportion (Freeman-Tukey)', 'proportion', 'freeman_tukey', ['proportion'], {
    comparative: false,
    nullValue: null,
  }),
  IR: info('IR', 'Incidence rate', 'rate', 'identity', ['rate'], { comparative: false, nullValue: null, cc: true }),
  IRLN: info('IRLN', 'Incidence rate (log scale)', 'rate', 'log', ['rate'], { comparative: false, nullValue: null, cc: true }),
  ZCOR: info('ZCOR', 'Correlation', 'correlation', 'fisher_z', ['correlation'], { comparative: false, nullValue: 0 }),
});

/** Every measure name, in registry order. */
export const MEASURE_LIST: readonly Measure[] = Object.freeze(Object.keys(MEASURES) as Measure[]);

/** True when `value` is a measure the engine knows. */
export function isMeasure(value: unknown): value is Measure {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(MEASURES, value);
}

/** Registry entry for a measure. Throws a TypeError for an unknown name (validate options first). */
export function measureInfo(measure: Measure): MeasureInfo {
  if (!isMeasure(measure)) throw new TypeError(`Unknown measure "${String(measure)}"`);
  return MEASURES[measure];
}

/** True for the measures computed from two-arm event counts (and pooled by Mantel-Haenszel too). */
export function isBinaryMeasure(measure: Measure): measure is BinaryMeasure {
  return measure === 'RR' || measure === 'OR' || measure === 'RD';
}

/** The data form of a study row; an untagged row is binary. */
export function studyKind(study: AnalysisStudy): StudyKind {
  return study.kind ?? 'binary';
}

/** True when `study`'s data form can give `measure`. */
export function studyFitsMeasure(study: AnalysisStudy, measure: Measure): boolean {
  return measureInfo(measure).studyKinds.includes(studyKind(study));
}

/** Measures a data form can give, in registry order (for the evidence page's measure choice). */
export function measuresForKind(kind: StudyKind): Measure[] {
  return MEASURE_LIST.filter((m) => MEASURES[m].studyKinds.includes(kind));
}

/**
 * Measures every one of `kinds` can give (the choices that keep every study in the analysis), in
 * registry order. Empty input gives every measure.
 */
export function measuresForKinds(kinds: readonly StudyKind[]): Measure[] {
  return MEASURE_LIST.filter((m) => kinds.every((k) => MEASURES[m].studyKinds.includes(k)));
}
